import {
  Alert,
  FormControl,
  FormControlLabel,
  FormLabel,
  List,
  ListItem,
  ListItemText,
  ListSubheader,
  Radio,
  RadioGroup,
  Stack,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useLanguage } from "../../../Language";
import { Controller, useForm } from "react-hook-form";
import { axios } from "../../../AxiosInstanceManager";
import { AccountHolder } from "../../../Account";
import { TitleAndPubDate } from "../TitleAndPubDate";
import {
  AvailabilitySource,
  AvailabilitySourceService,
} from "../../../../contexts/EResourceAvailabilityContext";
import { ResourceView } from "@tlc/ls2pac/dto/ResourceView";
import { ResourceDWR } from "@tlc/ls2pac/dto/ResourceDWR";
import { EResourceFormat } from "@tlc/ls2pac/dto/EResourceFormat";
import { HostSystemItemView } from "@tlc/ls2pac/dto/HostSystemItemView";
import { FormDialog } from "@tlc/ui/dialog/FormDialog";
import { DialogWithDataProps } from "@tlc/ui/dialog/useDialog";
import { useToast } from "@tlc/ui/toast";

interface EResourceCheckout {
  service: string;
  titleId: string;
  patronId: string;
  format: string;
}

export interface EResourceCheckoutResponse {
  service: AvailabilitySourceService; // string
  itemId: string;
  success: boolean;
  holdsPosition: number;
  itemNotAvailable: boolean;
  checkedOut: boolean;
  accountLocked: boolean;
  url: string;
  expirationDate: string; // Date;
  maxedOut: boolean;
  autoSync: boolean;
  format: string;
  actionEnabled: boolean;
  useRedirectForUrl: boolean;
  invalidUser: boolean;
  formatServiceToken: string;
  onHoldForAnotherUser: boolean;
  renewalsNotAllowed: boolean;
}

interface DownloadableCheckoutFormInput {
  format: string;
}

export interface DownloadableCheckoutData {
  resource: ResourceView | ResourceDWR;
  source: Pick<AvailabilitySource, "service" | "itemId" | "formatInfo">;
}

interface DownloadableCheckoutDialogProps
  extends DialogWithDataProps<DownloadableCheckoutData> {
  onSuccess: (response: EResourceCheckoutResponse) => void;
}
export const DownloadableCheckoutDialog = ({
  open,
  onClose,
  onSuccess,
  data: {
    resource,
    source: { service, itemId, formatInfo },
  },
}: DownloadableCheckoutDialogProps) => {
  const { translate, translateToken } = useLanguage();
  const { showToast } = useToast();
  const formMethods = useForm<DownloadableCheckoutFormInput>({
    defaultValues: {},
  });
  const { handleSubmit, reset } = formMethods;
  const defaultFormat = formatInfo?.[0]?.formatId || "";

  useEffect(() => {
    if (!open) return;
    // we only set the default format for baker and taylor
    if (service === AvailabilitySourceService.bakertaylor) {
      reset({ format: defaultFormat });
    }
  }, [open, service, defaultFormat, reset]);

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
  }, [open, setError]);

  const getFormatName = ({ formatToken, formatId }: EResourceFormat) => {
    if (formatToken && formatToken !== "<InvalidEResourceFormat>") {
      return translateToken(formatToken);
    }
    return formatId;
  };

  const getBakerAndTaylorForm = () => {
    return (
      <FormControl component="fieldset" variant="standard">
        <FormLabel component="legend">
          {translate("eResourceCheckoutFormatLabel")}
        </FormLabel>
        <Controller<DownloadableCheckoutFormInput>
          name="format"
          control={formMethods.control}
          render={({ field }) => (
            <RadioGroup {...field}>
              {formatInfo?.map((fi) => (
                <FormControlLabel
                  disabled={loading}
                  key={fi.formatId}
                  value={fi.formatId}
                  control={<Radio />}
                  label={getFormatName(fi)}
                />
              ))}
            </RadioGroup>
          )}
        />
      </FormControl>
    );
  };

  const getDialogContent = () => {
    if (service === AvailabilitySourceService.bakertaylor) {
      if (formatInfo?.length > 1) {
        return getBakerAndTaylorForm();
      }
    }

    return (
      <List>
        <ListSubheader>{translate("thisTitleIsAvailableAs")}</ListSubheader>
        {formatInfo?.map((fi) => (
          <ListItem key={fi.formatId}>
            <ListItemText primary={getFormatName(fi)} />
          </ListItem>
        ))}
      </List>
    );
  };

  const handleError = (data: EResourceCheckoutResponse): string => {
    if (data.accountLocked) {
      return translate("eResourceAccountLockedMessage");
    }
    if (data.invalidUser) {
      return translate("eResourceInvalidUserMessage");
    }
    if (data.maxedOut) {
      return translate("PatronCannotCheckoutTitleToken");
    }
    if (data.onHoldForAnotherUser) {
      return translate("eResourceLoanIsOnHold");
    }
    if (data.checkedOut) {
      return translate("eResourceAlreadyCheckedOutErrorMessage");
    }
    if (data.itemNotAvailable) {
      return translate("eResourceItemNotAvailable");
    }
    if (data.renewalsNotAllowed) {
      return translate("eResourceLoanRenewalNotPermitted");
    }
    return translate("eResourceActionFailure");
  };

  const onSubmit = async ({ format }: DownloadableCheckoutFormInput) => {
    const payload: Omit<EResourceCheckout, "patronId"> = {
      format,
      service: service,
      titleId: itemId,
    };
    try {
      setLoading(true);
      const { data } = await axios.post<EResourceCheckoutResponse>(
        `eResource/checkout`,
        payload,
      );
      if (data.success) {
        setError(null);
        // update counts
        AccountHolder.fetchAccountSummaryAndPatron(true);
        onSuccess(data);
      } else {
        setError(() => handleError(data));
      }
    } catch (e: unknown) {
      console.error(e);
      setError(translate("eResourceActionFailure"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && !formatInfo?.length) {
      showToast(translate("eResourceNoFormatsMessage"), "error");
      onClose();
    }
  }, [showToast, translate, formatInfo?.length, open, onClose]);

  if (!formatInfo?.length) {
    return null;
  }

  return (
    <FormDialog
      formId="downloadable-checkout-form"
      open={open}
      onClose={onClose}
      confirmButtonText={translate("ok")}
      submitButtonDisabled={!!error}
      isSubmitting={loading}
      headline={translate("checkoutEResourceDialogTitle")}
    >
      <form
        id="downloadable-checkout-form"
        noValidate
        onSubmit={handleSubmit(onSubmit)}
      >
        <Stack gap={2}>
          <TitleAndPubDate resource={resource} />
          {getDialogContent()}
          {!!error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </form>
    </FormDialog>
  );
};
